'use strict';

import AuthStore from '../stores/AuthStore';
import { getConfig } from './Env';

// Time phases for progress messages
export const JOB_POLLING_PHASES = {
    INITIAL: 8000,
    PROCESSING: 15000,
    EXTENDED: 25000,
    TIMEOUT: 40000
};

// Enhanced defaults with just the essentials
export const POLLING_DEFAULTS = {
    MAX_ATTEMPTS: 15,
    INITIAL_DELAY: 1000,
    MAX_DELAY: 4000,
    BACKOFF_MULTIPLIER: 1.4,
    JITTER_RANGE: 200  // Simple jitter to prevent thundering herd
};

// Standard progress messages
export const JOB_MESSAGES = {
    DEFAULT: 'Processing your request...',
    INITIAL: 'This is taking a moment longer than usual...',
    PROCESSING: 'Still processing your request...',
    EXTENDED: 'Thank you for your patience, almost done...',
    TIMEOUT: 'This is taking longer than expected. Please wait...'
};

/**
 * Poll a job until it completes or fails - Enhanced with minimal, high-impact improvements
 *
 * @param {string} jobId - The job UUID to poll
 * @param {Object} options - Configuration options
 * @param {Function} options.onProgress - Callback for progress updates (optional)
 * @param {AbortSignal} options.signal - For cancellation support (optional)
 * @param {number} options.maxAttempts - Maximum polling attempts
 * @param {number} options.initialDelay - Initial delay in milliseconds
 * @param {number} options.maxDelay - Maximum delay between polls
 * @param {number} options.backoffMultiplier - Delay multiplier for exponential backoff
 *
 * @returns {Promise<Object>} The completed job object
 * @throws {Error} If job fails, times out, or network error occurs
 */
export async function pollJobStatus(jobId, options = {}) {
    const {
        onProgress = () => { },
        signal = null,
        maxAttempts = POLLING_DEFAULTS.MAX_ATTEMPTS,
        initialDelay = POLLING_DEFAULTS.INITIAL_DELAY,
        maxDelay = POLLING_DEFAULTS.MAX_DELAY,
        backoffMultiplier = POLLING_DEFAULTS.BACKOFF_MULTIPLIER
    } = options;

    if (!jobId) {
        throw new Error('Job ID is required');
    }

    // Early abort check
    if (signal?.aborted) {
        throw new Error('Operation was cancelled');
    }

    const jobUrl = `${getConfig('users_api')}/jobs/${jobId}`;
    let attempts = 0;
    let delay = initialDelay;
    let consecutiveNetworkErrors = 0;
    const startTime = Date.now();

    // Simple jitter function - prevents all clients hitting at exact same time
    const addJitter = (baseDelay) => {
        const jitter = Math.random() * POLLING_DEFAULTS.JITTER_RANGE;
        return Math.round(baseDelay + jitter);
    };

    // Initial fetch
    let job;
    try {
        job = await AuthStore.fetch(jobUrl);
    } catch (error) {
        throw new Error('Failed to fetch job status');
    }

    if (!job) {
        throw new Error('Failed to fetch job status');
    }

    // Poll until completion
    while (job?.status !== 'completed' && job?.status !== 'failed' && attempts < maxAttempts) {
        // Check for cancellation
        if (signal?.aborted) {
            throw new Error('Operation was cancelled');
        }

        attempts++;

        // Notify progress
        const elapsed = Date.now() - startTime;
        onProgress(elapsed, attempts);

        // Wait with jitter and cancellation support
        const delayWithJitter = addJitter(delay);

        try {
            await new Promise((resolve, reject) => {
                const timer = setTimeout(resolve, delayWithJitter);

                // Handle abort signal
                if (signal) {
                    const onAbort = () => {
                        clearTimeout(timer);
                        reject(new Error('Operation was cancelled'));
                    };

                    if (signal.aborted) {
                        clearTimeout(timer);
                        reject(new Error('Operation was cancelled'));
                        return;
                    }

                    signal.addEventListener('abort', onAbort, { once: true });
                }
            });
        } catch (error) {
            throw error; // Re-throw cancellation
        }

        // Calculate next delay with exponential backoff
        delay = Math.min(delay * backoffMultiplier, maxDelay);

        // Fetch job status
        try {
            job = await AuthStore.fetch(jobUrl);
            consecutiveNetworkErrors = 0; // Reset on success
        } catch (fetchError) {
            consecutiveNetworkErrors++;

            // Allow up to 3 consecutive network errors before giving up
            if (consecutiveNetworkErrors >= 3) {
                throw new Error('Multiple network errors occurred. Please check your connection and try again.');
            }

            // Network error - retry if we have attempts left
            if (attempts < maxAttempts - 1) {
                console.warn(`Network error during job polling (${consecutiveNetworkErrors}/3):`, fetchError);
                continue;
            }

            throw new Error('Network error while checking job status. Please try again.');
        }

        // Check if job fetch returned null/undefined
        if (!job) {
            throw new Error('Unable to check job status. Please try again.');
        }
    }

    // Check final status
    if (attempts >= maxAttempts && job?.status !== 'completed' && job?.status !== 'failed') {
        throw new Error('The server is taking too long to respond. Please try again in a moment.');
    }

    if (job?.status === 'failed') {
        throw new Error(job.error || job.output?.error || 'Operation failed. Please try again.');
    }

    if (job?.status !== 'completed') {
        throw new Error('Unable to complete operation. Please try again.');
    }

    return job;
}

/**
 * Get suggested message based on elapsed time
 */
export function getJobProgressMessage(elapsed) {
    if (elapsed < JOB_POLLING_PHASES.INITIAL) {
        return JOB_MESSAGES.DEFAULT;
    } else if (elapsed < JOB_POLLING_PHASES.PROCESSING) {
        return JOB_MESSAGES.INITIAL;
    } else if (elapsed < JOB_POLLING_PHASES.EXTENDED) {
        return JOB_MESSAGES.PROCESSING;
    } else if (elapsed < JOB_POLLING_PHASES.TIMEOUT) {
        return JOB_MESSAGES.EXTENDED;
    } else {
        return JOB_MESSAGES.TIMEOUT;
    }
}

/**
 * Poll a job with automatic message updates
 */
export async function pollJobWithMessages(jobId, onMessageUpdate, options = {}) {
    return pollJobStatus(jobId, {
        ...options,
        onProgress: (elapsed, attempts) => {
            // Call custom onProgress if provided
            if (options.onProgress) {
                options.onProgress(elapsed, attempts);
            }
            // Also update message
            const message = getJobProgressMessage(elapsed);
            onMessageUpdate(message);
        }
    });
}

/**
 * Create a cancellable job poller - The ONE new feature that actually matters
 */
export function createCancellableJobPoller(jobId, options = {}) {
    const controller = new AbortController();

    const promise = pollJobStatus(jobId, {
        ...options,
        signal: controller.signal
    });

    return {
        promise,
        cancel: () => controller.abort(),
        cancelled: () => controller.signal.aborted
    };
}

/**
 * Create a progress handler function for specific job types
 */
export function createProgressHandler(messageMap, updateCallback) {
    const thresholds = Object.keys(messageMap)
        .map(Number)
        .sort((a, b) => a - b);

    return (elapsed) => {
        let message = messageMap[0] || JOB_MESSAGES.DEFAULT;

        for (const threshold of thresholds) {
            if (elapsed >= threshold) {
                message = messageMap[threshold];
            } else {
                break;
            }
        }

        updateCallback(message);
    };
}